import { bffApi } from '@shared/service/bff'
import { WalletException } from '@injectivelabs/exceptions'
import {
  requestBffApi,
  requestAuthenticatedBffApi
} from '@/app/services/bffRequest'
import type { BffStartBody, BffAuthorizeBody } from '@/types/bff'

export class AuthService {
  async signNonce({
    signer,
    privateKey
  }: {
    signer: string
    privateKey: string
  }) {
    const { nonce } = await this.start(signer)

    const message = `Nonce: ${nonce} \n Wallet address: ${signer}`
    const normalizedKey = (
      privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`
    ) as `0x${string}`

    const [{ privateKeyToAccount }, { getEthereumAddress }] =
      await Promise.all([
        import('viem/accounts'),
        import('@injectivelabs/sdk-ts/utils/address')
      ])
    const account = privateKeyToAccount(normalizedKey)
    const expectedEthAddress = getEthereumAddress(signer).toLowerCase()

    if (account.address.toLowerCase() !== expectedEthAddress) {
      throw new WalletException(
        new Error('Signer address does not match the provided private key')
      )
    }

    const signature = await account.signMessage({ message })

    return { data: message, signature }
  }

  async getMe() {
    const { data, response } = await requestAuthenticatedBffApi({
      request: (requestOptions) => bffApi.api.v1.user.me.get(requestOptions)
    })

    if (response.status === 401 || response.status === 404) {
      return null
    }

    if (!response.ok || !data?.data) {
      throw new Error(`Me failed (${response.status})`)
    }

    return data.data
  }

  async verifyEntryCode(code: string) {
    const { data, response } = await requestAuthenticatedBffApi({
      request: (requestOptions) =>
        bffApi.api.v2.entry_code.verify.post({
          ...requestOptions,
          body: { code }
        })
    })

    if (!response.ok || !data?.data) {
      throw new Error(String(response.status))
    }

    return data.data
  }

  async start(userAddress: BffStartBody['userAddress']) {
    const { data, response } = await requestBffApi({
      request: () =>
        bffApi.api.v1.authorization.start.post({
          body: { userAddress }
        })
    })

    if (!response.ok || !data?.data) {
      throw new Error(`Auth start failed (${response.status})`)
    }

    return data.data
  }

  async authorize(body: BffAuthorizeBody) {
    const { data, response } = await requestBffApi({
      request: () =>
        bffApi.api.v1.authorization.post({
          body
        })
    })

    if (!response.ok || !data?.data) {
      throw new Error(`Authorize failed (${response.status})`)
    }

    return data.data
  }

  async getProfile() {
    const { data, response } = await requestAuthenticatedBffApi({
      request: (requestOptions) => bffApi.api.v1.profile.post(requestOptions)
    })

    if (!response.ok || !data?.address) {
      throw new Error(`Profile failed (${response.status})`)
    }

    return data
  }

  async logout() {
    const { response } = await requestBffApi({
      request: () => bffApi.api.v1.authorization.logout.post({})
    })

    return response.ok
  }
}
